import type {
  ProductScriptFormValues,
  ProductShotImageGenerationParams,
} from './model';
import type { ProductShotImageTaskRecord } from './utils';

export interface ProductAnalysisResultDraft {
  form_values?: ProductScriptFormValues;
  image_tasks?: ProductShotImageTaskRecord[];
  image_generation_params?: Record<number, ProductShotImageGenerationParams>;
}

export function getProductAnalysisResultDraftStorageKey(
  userId: string,
  task_id: string,
) {
  return `${userId}_v2_product_analysis_result_${task_id}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isProductAnalysisResultDraft(
  value: unknown,
): value is ProductAnalysisResultDraft {
  if (!isRecord(value)) {
    return false;
  }

  const { form_values, image_tasks, image_generation_params } = value;

  if (
    form_values !== undefined &&
    (!isRecord(form_values) ||
      !Array.isArray(form_values['shots']) ||
      !form_values['shots'].every(
        (shot) =>
          isRecord(shot) &&
          typeof shot['script'] === 'string' &&
          typeof shot['image_instructions'] === 'string',
      ))
  ) {
    return false;
  }

  if (
    image_tasks !== undefined &&
    (!Array.isArray(image_tasks) ||
      !image_tasks.every(
        (task) =>
          isRecord(task) &&
          typeof task['shot_position'] === 'number' &&
          isRecord(task['initial_task']) &&
          typeof task['initial_task']['id'] === 'string',
      ))
  ) {
    return false;
  }

  if (
    image_generation_params !== undefined &&
    (!isRecord(image_generation_params) ||
      !Object.values(image_generation_params).every(
        (params) =>
          isRecord(params) &&
          Array.isArray(params['reference_images']) &&
          params['reference_images'].every(
            (file) =>
              isRecord(file) &&
              typeof file['name'] === 'string' &&
              typeof file['type'] === 'string' &&
              typeof file['url'] === 'string',
          ) &&
          (params['aspect_ratio'] === null ||
            typeof params['aspect_ratio'] === 'string') &&
          typeof params['model'] === 'string',
      ))
  ) {
    return false;
  }

  return true;
}
